import React, { Fragment, useCallback, useState } from "react";
import { blurActiveElement } from "@danishagro/shared/src/helpers/blurActiveElement.helper";
import { cn } from "@danishagro/shared/src/helpers/classNames.helper";
import { DA_ButtonLink } from "@danishagro/shared/src/components/atoms/ButtonLink/ButtonLink.component";
import { DA_Icon, DA_IconNames } from "@danishagro/shared/src/components/atoms/Icon/Icon.component";
import { DA_SwipeOrRotate } from "@danishagro/shared/src/components/atoms/SwipeOrRotate/SwipeOrRotate.component";
import { useTranslations } from "@danishagro/shared/src/contexts/translations/translations.context";
import { DA_Checkbox } from "../Checkbox/Checkbox.component";
import useNestedTables from "./hooks/useNestedTables.hook";
import {
    Column,
    DA_TableProps,
    TableRowNestedReactNode,
    TableRowNestedTable,
    TableRowSimple,
} from "./TableProps";
import S from "./Table.module.scss";

export const DA_Table = ({
    columns,
    data,
    prefix,
    postfix,
    fadeBottom,
    sortByColumnIndex,
    onSortChange,
    sortAscending,
    disabled,
    dimmed,
    disableEqualWidth,
    addDownloadDocument,
    downloadCheckbox = false,
    selectAll,
    excludedDocuments,
    selectedDocuments,
    isInvoicesPage,
    isArchivePage,
    isDownloadable,
    colorSeparatedRows,
    stickyHeader,
    tableHeight,
    isScrollableOnMobile,
    noSwipeOrRotate = false,
}: DA_TableProps) => {
    const { getDictionaryString } = useTranslations();

    const [expandedRows, setExpandedRows] = useState({});

    // Function to toggle row expansion
    const toggleRow = (id) => {
        setExpandedRows((prev) => {
            const newState = { ...prev, [id]: !prev[id] };
            return newState;
        });
    };

    // Expand cells
    const renderUrlCell = (
        rowId,
        urls,
        showMoreTranslation,
        showLessTranslation,
        cellFullWidth = false,
        cellClassName,
        isOpen,
        defaultVisibleItems,
        sameLine = false
    ) => {
        const isExpanded =
            expandedRows[rowId] ?? (isOpen && urls.length > (defaultVisibleItems || 1));
        const visibleItemCount = defaultVisibleItems || 1;
        const hasMoreItems = urls.length > visibleItemCount;

        // Determine the URLs to display based on expansion state
        const displayedUrls = isExpanded ? urls : urls.slice(0, visibleItemCount);

        return (
            <td
                key={`${rowId}-${urls}`}
                className={cn(
                    S.cell,
                    S.bodyCell,
                    disableEqualWidth && S.disableEqualWidth,
                    cellClassName && cellClassName
                )}
            >
                <div
                    className={cn(
                        !isExpanded ? S.cellNotExpanded : S.cellExpanded,
                        isExpanded && sameLine && S.cellExpandedUrlLineShort
                    )}
                >
                    {displayedUrls.map((url, index) => (
                        <div
                            key={`${rowId}-${index}`}
                            title={url}
                            className={cn(
                                S.urlLine,
                                isExpanded && S.urlLineExpanded,
                                cellFullWidth && S.cellFullWidth,
                                sameLine && S.urlLineShort
                            )}
                        >
                            {url}
                        </div>
                    ))}

                    {/* Show the expand button and urlAmountBadge if there are more items */}
                    {!isExpanded && hasMoreItems && (
                        <div className={S.additionalInfo}>
                            <div className={S.urlAmountBadge}>
                                <span>+{urls.length - visibleItemCount}</span>
                            </div>
                            <button className={S.expandBtn} onClick={() => toggleRow(rowId)}>
                                <DA_Icon
                                    className={S.expandBtnIcon}
                                    name={DA_IconNames.ChevronSmallDown}
                                />
                                <span>
                                    {getDictionaryString(showMoreTranslation || "showMore")}
                                </span>
                            </button>
                        </div>
                    )}
                </div>

                {/* Show the collapse button when expanded */}
                {isExpanded && (
                    <div className={S.expandBtnWrapper}>
                        <button className={S.expandBtn} onClick={() => toggleRow(rowId)}>
                            <DA_Icon
                                className={S.expandBtnIcon}
                                name={DA_IconNames.ChevronSmallUp}
                            />
                            <span>{getDictionaryString(showLessTranslation || "hide")}</span>
                        </button>
                    </div>
                )}
            </td>
        );
    };

    const getWidths = useCallback((width: number | string) => {
        if (!width) return undefined;

        const widthWithUnit = typeof width === "string" ? width : `${width}px`;

        return {
            width: widthWithUnit,
            minWidth: widthWithUnit,
            maxWidth: widthWithUnit,
        };
    }, []);

    const printHeadCellContent = useCallback(
        (column: Column, columnIndex: number) => {
            const hasAlignRightClass = column.alignRight ? S.alignRight : undefined;

            return column.sortable ? (
                <button
                    className={cn(S.headCellContent, S.headCellButton, hasAlignRightClass)}
                    type="button"
                    onClick={() => {
                        if (sortByColumnIndex === columnIndex) {
                            onSortChange(sortByColumnIndex, !sortAscending);
                        } else {
                            onSortChange(columnIndex, false);
                        }
                        blurActiveElement();
                    }}
                >
                    {column.title}
                    <DA_Icon
                        name={DA_IconNames.TriangleSmallDown}
                        className={cn(
                            S.sortArrow,
                            columnIndex === sortByColumnIndex && S.active,
                            columnIndex === sortByColumnIndex && sortAscending && S.rotated
                        )}
                    />
                </button>
            ) : (
                <div className={cn(S.headCellContent, hasAlignRightClass)}>{column.title}</div>
            );
        },
        [sortByColumnIndex, sortAscending, onSortChange]
    );

    const { currentExpandedRows, onExpandButtonClick } = useNestedTables();

    const isDocumentSelected = (id, type) => {
        return selectedDocuments.has(`${id}-${type}`);
    };

    const isDocumentExcluded = (id, type) => {
        return excludedDocuments.has(`${id}-${type}`);
    };

    const renderSimpleTableRow = (row: TableRowSimple, rowIndex: number) => {
        // First, ensure that the value is indeed a string
        const idValue = typeof row[2]?.value === "string" ? row[2].value : "";
        const typeValue = typeof row[0]?.raw === "string" ? row[0].raw : "";

        return (
            <tr key={`row-${rowIndex}`} className={cn(S.row, rowIndex % 2 && S.even)}>
                {(isInvoicesPage || isArchivePage || isDownloadable) && downloadCheckbox && (
                    <td className={S.centered}>
                        <DA_Checkbox
                            onChange={() => addDownloadDocument(idValue, typeValue)}
                            label=""
                            className={S.tableCheckBox}
                            name={idValue}
                            checked={
                                selectAll
                                    ? !isDocumentExcluded(idValue, typeValue)
                                    : isDocumentSelected(idValue, typeValue)
                            }
                        />
                    </td>
                )}
                {row.map((cell, cellIndex) => {
                    if (
                        cell.isUrlCell &&
                        typeof cell.value === "object" &&
                        Array.isArray(cell.value)
                    ) {
                        return renderUrlCell(
                            rowIndex,
                            cell.value,
                            cell.showMoreTranslation,
                            cell.showLessTranslation,
                            cell.cellFullWidth,
                            cell.className,
                            cell.isOpen,
                            cell.defaultVisibleItems,
                            cell.sameLine
                        );
                    } else {
                        // Standard cell rendering
                        return (
                            <td
                                key={`cell-${cellIndex}`}
                                colSpan={cell.colSpan}
                                ref={cell.ref || null}
                                className={cn(
                                    S.cell,
                                    S.bodyCell,
                                    disableEqualWidth && S.disableEqualWidth,
                                    cell.alignRight && S.alignRight,
                                    cell.alignCenter && S.alignCenter,
                                    cell.bold && S.bold,
                                    cell.className
                                )}
                            >
                                {cell.value}
                            </td>
                        );
                    }
                })}
            </tr>
        );
    };

    const renderNestedTableReactNodeRow = (row: TableRowNestedReactNode, rowIndex: number) => {
        // TODO: Create a better Identification on a row.
        const hasExpandedRow = currentExpandedRows?.find(
            (x) =>
                `${x?.data?.[0]?.value}_${x?.data?.[1]?.value}` ===
                `${row?.data?.[0]?.value}_${row?.data?.[1]?.value}`
        );

        return (
            <Fragment key={`row-${rowIndex}`}>
                <tr className={cn(S.row, rowIndex % 2 && S.even)}>
                    {row.data.map((cell, cellIndex) => (
                        <td
                            key={`cell-${cellIndex}`}
                            colSpan={cell.colSpan}
                            className={cn(
                                S.cell,
                                S.bodyCell,
                                cell.alignRight && S.alignRight,
                                cell.alignCenter && S.alignCenter,
                                cell.bold && S.bold
                            )}
                        >
                            {/** Action */}
                            {cell.action === "NESTED_BUTTON" ? (
                                <DA_ButtonLink
                                    icon={
                                        hasExpandedRow
                                            ? DA_IconNames.ChevronSmallUp
                                            : DA_IconNames.ChevronSmallDown
                                    }
                                    title={
                                        hasExpandedRow
                                            ? getDictionaryString("TableNestedButtonCollapse")
                                            : getDictionaryString("TableNestedButtonExpand")
                                    }
                                    onClick={() => onExpandButtonClick(row)}
                                    noUnderline
                                />
                            ) : null}

                            {/** Value */}
                            {cell.value ? <>{cell.value}</> : null}
                        </td>
                    ))}
                </tr>

                {/** Extra: ReactNode */}
                {row.extra && React.isValidElement(row.extra) && (
                    <tr className={cn(S.row, rowIndex % 2 && S.even)}>
                        <td colSpan={columns.length} className={cn(S.cell, S.bodyCell)}>
                            {row.extra}
                        </td>
                    </tr>
                )}
            </Fragment>
        );
    };

    const renderTableNestedTableRow = (row: TableRowNestedTable, rowIndex: number) => {
        // TODO: Create a better Identification on a row.
        const hasExpandedRow = currentExpandedRows?.find(
            (x) =>
                `${x?.data?.[0]?.value}_${x?.data?.[1]?.value}` ===
                `${row?.data?.[0]?.value}_${row?.data?.[1]?.value}`
        );

        // Calculate the background color based on row index and expansion status
        const rowBackgroundColorClass = colorSeparatedRows
            ? rowIndex % 2 === 0
                ? hasExpandedRow
                    ? S.green
                    : ""
                : hasExpandedRow
                ? S.lightgreen
                : ""
            : "";

        return (
            <Fragment key={`row-${rowIndex}`}>
                <tr
                    className={cn(
                        S.row,
                        {
                            [S.even]: rowIndex % 2 === 0 && colorSeparatedRows,
                            [colorSeparatedRows && S.expandedMain]: hasExpandedRow,
                        },
                        rowBackgroundColorClass
                    )}
                    tabIndex={rowIndex}
                >
                    {row.data.map((cell, cellIndex) => (
                        <td
                            key={`cell-${cellIndex}`}
                            colSpan={cell.colSpan}
                            className={cn(
                                S.cell,
                                S.bodyCell,
                                disableEqualWidth && S.disableEqualWidth,
                                cell.alignRight && S.alignRight,
                                cell.alignCenter && S.alignCenter,
                                cell.bold && S.bold,
                                cell.className
                            )}
                        >
                            {/** Action */}
                            {cell.action === "NESTED_BUTTON" ? (
                                <DA_ButtonLink
                                    icon={
                                        hasExpandedRow
                                            ? DA_IconNames.ChevronSmallUp
                                            : DA_IconNames.ChevronSmallDown
                                    }
                                    title={
                                        hasExpandedRow
                                            ? getDictionaryString("TableNestedButtonCollapse")
                                            : getDictionaryString("TableNestedButtonExpand")
                                    }
                                    onClick={() => onExpandButtonClick(row)}
                                    noUnderline
                                />
                            ) : null}

                            {/** Value */}
                            {cell.value ? <>{cell.value}</> : null}
                        </td>
                    ))}
                </tr>

                {/** Extra: Table */}
                {row.extra &&
                    Object.prototype.hasOwnProperty.call(row.extra, "data") &&
                    hasExpandedRow && (
                        <tr
                            className={cn(
                                S.row,
                                {
                                    [S.even]: rowIndex % 2 === 0 && colorSeparatedRows,
                                    [colorSeparatedRows && S.expandedSub]: hasExpandedRow,
                                },
                                rowBackgroundColorClass
                            )}
                            tabIndex={rowIndex}
                        >
                            <td colSpan={columns.length} className={S.tableNestedWrapperCell}>
                                <table className={S.tableNested}>
                                    {/** Head */}
                                    <thead className={S.nestedHead}>
                                        <tr>
                                            {row.extra?.columns?.[0] &&
                                                row.extra?.columns.map((column, columnIndex) => (
                                                    <th
                                                        key={`column-${columnIndex}`}
                                                        colSpan={column.colSpan}
                                                        className={cn(
                                                            S.cell,
                                                            S.headCell,
                                                            disableEqualWidth &&
                                                                S.disableEqualWidth,
                                                            column.alignRight && S.alignRight,
                                                            column.alignCenter && S.alignCenter
                                                        )}
                                                    >
                                                        {printHeadCellContent(column, columnIndex)}
                                                    </th>
                                                ))}
                                        </tr>
                                    </thead>

                                    {/** Body */}
                                    <tbody className={S.body}>
                                        {row.extra?.data.map((nestedRow, nestedRowIndex) => (
                                            <tr key={`row-${nestedRowIndex}`} className={cn(S.row)}>
                                                {nestedRow.map((cell, cellIndex) => (
                                                    <td
                                                        key={`cell-${cellIndex}`}
                                                        colSpan={cell.colSpan}
                                                        className={cn(
                                                            S.cell,
                                                            S.bodyCell,
                                                            cell.alignRight && S.alignRight,
                                                            cell.alignCenter && S.alignCenter,
                                                            cell.bold && S.bold,
                                                            cell.className
                                                        )}
                                                    >
                                                        {cell.value}
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </td>
                        </tr>
                    )}
                {colorSeparatedRows && hasExpandedRow && <div className={S.seperator} />}
            </Fragment>
        );
    };

    return (
        <div className={cn(S.outerWrapper, disabled && S.disabled)}>
            {!noSwipeOrRotate && <DA_SwipeOrRotate />}

            <div
                className={cn(
                    S.wrapper,
                    data.length > 0 && S.scrollable,
                    isScrollableOnMobile && S.isScrollableOnMobile
                )}
            >
                <div
                    className={cn(S.innerWrapper, fadeBottom && S.fadeBottom)}
                    style={{
                        maxHeight: stickyHeader && tableHeight ? `${tableHeight}px` : undefined,
                    }}
                >
                    {/** Table */}
                    <table className={S.table}>
                        {/** Prefix */}
                        {prefix && (
                            <thead>
                                <tr>
                                    {prefix.map((cell, cellIndex) => (
                                        <td
                                            key={`cell-${cellIndex}`}
                                            colSpan={cell.colSpan}
                                            className={cn(
                                                S.cell,
                                                S.prefixCell,
                                                S.bodyCell,
                                                disableEqualWidth && S.disableEqualWidth,
                                                cell.alignRight && S.alignRight,
                                                cell.bold && S.bold,
                                                cell.className
                                            )}
                                        >
                                            {cell.value}
                                        </td>
                                    ))}
                                </tr>
                            </thead>
                        )}

                        {/** Header */}
                        <thead
                            className={cn(
                                S.head,
                                dimmed && S.dimmed,
                                stickyHeader && S.stickyHeader
                            )}
                        >
                            <tr>
                                {columns.map((column, columnIndex) => (
                                    <th
                                        key={`column-${columnIndex}`}
                                        style={getWidths(column.width)}
                                        className={cn(
                                            S.cell,
                                            S.headCell,
                                            disableEqualWidth && S.disableEqualWidth,
                                            column.alignRight && S.alignRight,
                                            column.alignCenter && S.alignCenter,
                                            column.className
                                        )}
                                    >
                                        {printHeadCellContent(column, columnIndex)}
                                    </th>
                                ))}
                            </tr>
                        </thead>

                        {/** Body */}
                        <tbody className={cn(S.body, dimmed && S.dimmed)}>
                            {data.map((row, rowIndex) => {
                                // a. Simple Table
                                if (Array.isArray(row)) {
                                    return renderSimpleTableRow(row as TableRowSimple, rowIndex);
                                }
                                // b. Nested Table: Extra as ReactNode
                                else if (!Array.isArray(row) && React.isValidElement(row.extra)) {
                                    return renderNestedTableReactNodeRow(
                                        row as TableRowNestedReactNode,
                                        rowIndex
                                    );
                                }
                                // c. Nested Table: Extra as Table
                                else if (
                                    !Array.isArray(row) &&
                                    row.extra &&
                                    Object.prototype.hasOwnProperty.call(row.extra, "data")
                                ) {
                                    return renderTableNestedTableRow(
                                        row as TableRowNestedTable,
                                        rowIndex
                                    );
                                }
                            })}

                            {/** Postfix */}
                            {postfix && (
                                <tr className={cn(data.length % 2 !== 0 && S.borderTop)}>
                                    {postfix.map((cell, cellIndex) => (
                                        <td
                                            key={`cell-${cellIndex}`}
                                            colSpan={cell.colSpan}
                                            className={cn(
                                                S.cell,
                                                S.bodyCell,
                                                disableEqualWidth && S.disableEqualWidth,
                                                cell.alignRight && S.alignRight,
                                                cell.bold && S.bold,
                                                cell.className
                                            )}
                                        >
                                            {cell.value}
                                        </td>
                                    ))}
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};
